import React, {
    useEffect,
    useRef,
    useState
} from "react";

import { toast } from "react-toastify";

import { useTranslation } from "react-i18next";

import {
    updatePhotoUserApp,
    downloadPhotoUserApp,
    getLoggedInUser
} from "../../pages/userapp/ServiceUserApp";


export default function PhotoUploader({

    id,

    photoUrl,

    onPhotoChange

}) {

    const { t } = useTranslation();

    // ==========================================================
    // Utilisateur connecté
    // ==========================================================

    const user = getLoggedInUser();

    // ==========================================================
    // Référence input file
    // ==========================================================

    const inputFileRef =
        useRef(null);

    // ==========================================================
    // Avatar par défaut
    // ==========================================================

    const defaultAvatar =
        "https://cdn-icons-png.flaticon.com/512/847/847969.png";

    // ==========================================================
    // Etats
    // ==========================================================

    const [preview, setPreview] =
        useState(photoUrl || "");

    const [dragActive, setDragActive] =
        useState(false);

    const [uploading, setUploading] =
        useState(false);


    // ==========================================================
    // Chargement de la photo depuis le serveur
    // ==========================================================

    const refreshPhoto = async () => {

        // ------------------------------------------------------
        // On utilise l'id reçu en paramètre
        // et non user.id
        // ------------------------------------------------------

        const userId =
            id || user?.id;

        if (!userId) {

            console.warn(
                "PhotoUploader : aucun ID utilisateur."
            );

            return;

        }

        try {

            console.log(
                "Chargement photo utilisateur :",
                userId
            );

            // --------------------------------------------------
            // Appel API
            // --------------------------------------------------

            const response =
                await downloadPhotoUserApp(
                    userId
                );

            console.log(
                "Photo response :",
                response
            );

            // --------------------------------------------------
            // Vérification HTTP
            // --------------------------------------------------

            if (
                response.status !== 200
            ) {

                console.warn(
                    "Erreur HTTP photo :",
                    response.status
                );

                return;

            }

            // --------------------------------------------------
            // Vérification Blob
            // --------------------------------------------------

            if (
                !(response.data instanceof Blob)
            ) {

                console.error(
                    "La réponse photo n'est pas un Blob :",
                    response.data
                );

                return;

            }

            // --------------------------------------------------
            // Vérification taille
            // --------------------------------------------------

            if (
                response.data.size === 0
            ) {

                console.warn(
                    "La photo reçue est vide."
                );

                return;

            }

            // --------------------------------------------------
            // Vérification Content-Type
            // --------------------------------------------------

            const contentType =
                response.headers?.[
                    "content-type"
                ] ||
                response.data.type ||
                "";

            console.log(
                "Photo Content-Type :",
                contentType
            );

            console.log(
                "Photo taille :",
                response.data.size
            );

            // --------------------------------------------------
            // Vérification que c'est bien une image
            // --------------------------------------------------

            if (
                !contentType.startsWith(
                    "image/"
                )
            ) {

                console.error(
                    "Le serveur ne retourne pas une image.",
                    contentType
                );

                return;

            }

            // --------------------------------------------------
            // Création Blob
            // --------------------------------------------------

            const imageBlob =
                new Blob(
                    [response.data],
                    {
                        type: contentType
                    }
                );

            // --------------------------------------------------
            // Création URL temporaire
            // --------------------------------------------------

            const imageUrl =
                URL.createObjectURL(
                    imageBlob
                );

            console.log(
                "URL photo créée :",
                imageUrl
            );

            // --------------------------------------------------
            // Mise à jour preview
            // --------------------------------------------------

            setPreview(
                imageUrl
            );

            // --------------------------------------------------
            // Notification parent
            // --------------------------------------------------

            if (onPhotoChange) {

                onPhotoChange(
                    imageUrl
                );

            }

        }
        catch (error) {

            console.error(
                "========== ERREUR PHOTO =========="
            );

            console.error(error);

            if (error.response) {

                console.error(
                    "Status :",
                    error.response.status
                );

                console.error(
                    "Headers :",
                    error.response.headers
                );

                console.error(
                    "Data :",
                    error.response.data
                );

            }

        }

    };


    // ==========================================================
    // CHARGEMENT INITIAL DE LA PHOTO
    // ==========================================================
    //
    // C'est cette partie qui manquait principalement.
    //
    // Dès que id est disponible, on récupère la photo
    // depuis le backend.
    //
    // ==========================================================

    useEffect(() => {

        const userId =
            id || user?.id;

        if (!userId) {

            return;

        }

        refreshPhoto();

    }, [id]);


    // ==========================================================
    // Synchronisation avec le parent
    // ==========================================================

    useEffect(() => {

        if (
            photoUrl &&
            !photoUrl.startsWith("blob:")
        ) {

            setPreview(
                photoUrl
            );

        }

    }, [photoUrl]);


    // ==========================================================
    // Nettoyage des Blob URL
    // ==========================================================

    useEffect(() => {

        return () => {

            if (
                preview &&
                preview.startsWith("blob:")
            ) {

                URL.revokeObjectURL(
                    preview
                );

            }

        };

    }, [preview]);


    // ==========================================================
    // Ouvrir sélecteur fichier
    // ==========================================================

    const openFileChooser = () => {

        if (
            inputFileRef.current
        ) {

            inputFileRef.current.click();

        }

    };


    // ==========================================================
    // Upload fichier
    // ==========================================================

    const uploadFile = async (
        file
    ) => {

        if (!file) {

            return;

        }

        // ------------------------------------------------------
        // Vérification type
        // ------------------------------------------------------

        if (
            !file.type.startsWith(
                "image/"
            )
        ) {

            toast.error(
                t(
                    "monProfile.invalidImage"
                )
            );

            return;

        }

        // ------------------------------------------------------
        // Taille maximale 5 Mo
        // ------------------------------------------------------

        if (
            file.size >
            5 * 1024 * 1024
        ) {

            toast.error(
                t(
                    "monProfile.maxPhotoSize"
                )
            );

            return;

        }

        try {

            setUploading(
                true
            );

            // --------------------------------------------------
            // FormData
            // --------------------------------------------------

            const formData =
                new FormData();

            formData.append(
                "image",
                file
            );

            // --------------------------------------------------
            // Upload
            // --------------------------------------------------

            await updatePhotoUserApp(
                id || user?.id,
                formData
            );

            toast.success(
                t(
                    "monProfile.photoUpdated"
                )
            );

            // --------------------------------------------------
            // Rechargement depuis serveur
            // --------------------------------------------------

            await refreshPhoto();

        }
        catch (error) {

            console.error(
                "Erreur upload photo :",
                error
            );

            toast.error(
                t(
                    "monProfile.photoUploadError"
                )
            );

        }
        finally {

            setUploading(
                false
            );

        }

    };


    // ==========================================================
    // Choix fichier
    // ==========================================================

    const handleFileChange = (
        event
    ) => {

        const files =
            event.target.files;

        if (
            files &&
            files.length > 0
        ) {

            uploadFile(
                files[0]
            );

        }

        // ------------------------------------------------------
        // Permet de sélectionner à nouveau le même fichier
        // ------------------------------------------------------

        event.target.value = "";

    };


    // ==========================================================
    // Drag Over
    // ==========================================================

    const handleDragOver = (
        event
    ) => {

        event.preventDefault();

        setDragActive(
            true
        );

    };


    // ==========================================================
    // Drag Leave
    // ==========================================================

    const handleDragLeave = () => {

        setDragActive(
            false
        );

    };


    // ==========================================================
    // Drop
    // ==========================================================

    const handleDrop = (
        event
    ) => {

        event.preventDefault();

        setDragActive(
            false
        );

        const files =
            event.dataTransfer.files;

        if (
            files &&
            files.length > 0
        ) {

            uploadFile(
                files[0]
            );

        }

    };


    // ==========================================================
    // Suppression locale
    // ==========================================================

    const removePhoto = () => {

        if (
            preview &&
            preview.startsWith("blob:")
        ) {

            URL.revokeObjectURL(
                preview
            );

        }

        setPreview(
            ""
        );

        if (
            onPhotoChange
        ) {

            onPhotoChange(
                ""
            );

        }

    };


    // ==========================================================
    // RENDU
    // ==========================================================

    return (

        <div className="photo-uploader">

            {/* ==================================================
                PHOTO
            ================================================== */}

            <div

                className={
                    `photo-container ${
                        dragActive
                            ? "drag-active"
                            : ""
                    }`
                }

                onDragOver={
                    handleDragOver
                }

                onDragLeave={
                    handleDragLeave
                }

                onDrop={
                    handleDrop
                }

            >

                <img

                    src={
                        preview ||
                        defaultAvatar
                    }

                    alt={
                        t(
                            "monProfile.profilePhoto"
                        )
                    }

                    className="profile-avatar"

                    onError={(event) => {

                        console.error(
                            "Impossible d'afficher la photo :",
                            preview
                        );

                        event.currentTarget.src =
                            defaultAvatar;

                    }}

                />


                {/* ==================================================
                    BOUTON CAMERA
                ================================================== */}

                <button

                    type="button"

                    className="camera-button"

                    title={
                        t(
                            "monProfile.editPhoto"
                        )
                    }

                    onClick={
                        openFileChooser
                    }

                    disabled={
                        uploading
                    }

                >

                    <i className="bi bi-camera-fill"></i>

                </button>

            </div>


            {/* ==================================================
                INPUT FILE
            ================================================== */}

            <input

                type="file"

                accept="image/*"

                ref={
                    inputFileRef
                }

                style={{
                    display: "none"
                }}

                onChange={
                    handleFileChange
                }

            />


            {/* ==================================================
                BOUTONS
            ================================================== */}

            <div className="mt-3 d-grid gap-2">


                {/* ==================================================
                    CHOISIR PHOTO
                ================================================== */}

                <button

                    type="button"

                    className="btn btn-outline-primary"

                    onClick={
                        openFileChooser
                    }

                    disabled={
                        uploading
                    }

                >

                    <i className="bi bi-upload me-2"></i>

                    {

                        uploading

                            ? t(
                                "monProfile.uploading"
                            )

                            : t(
                                "monProfile.choosePhoto"
                            )

                    }

                </button>


                {/* ==================================================
                    SUPPRIMER
                ================================================== */}

                <button

                    type="button"

                    className="btn btn-outline-danger"

                    onClick={
                        removePhoto
                    }

                    disabled={
                        uploading
                    }

                >

                    <i className="bi bi-trash me-2"></i>

                    {
                        t(
                            "monProfile.removePhoto"
                        )
                    }

                </button>

            </div>

        </div>

    );

}